import { useTryIt } from '@/hooks/use-try-it.hook';
import { TryRequestFailure } from '@/containers/agents/try-chat-card.component';
import { TryChatCard } from '@/containers/agents/try-chat-card.component';
import { splitPersonName } from '@/lib/name-normalizer';
import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { GoogleSheetReadCard } from './google-sheet-read-card.component';
import { GoogleDocReadCard } from './google-doc-read-card.component';
import { SheetTriggerPickCard } from '@/containers/agents/sheet-trigger-pick-card.component';
import { FirstRunOptionsCard } from '@/containers/onboarding/first-run/first-run-options-card.component';
import { GoogleDocPickCard } from '@/containers/agents/google-doc-pick-card.component';
import {
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardCost,
  CardFooter,
  CardFree,
  CardLinkButton,
  CardMeta,
  CardRow,
  CardRows,
  CardShortOfCredits,
  CardStatus,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import { CreditAmount } from '@/components/ui/chips/credit-amount';
import {
  ConnectionRequiredCard,
  DuplicateMergeStartedCard,
  OpenRunLink,
  PlayProposalCard,
  QuickRepliesPart,
  ReviewDuplicatesLink,
  RunDiagnosisActions,
} from '@/components/agent-chat/agent-cards.component';
import { GoogleSheetImportCard } from '@/components/agent-chat/google-sheet-import-card.component';
import { PastedRowsImportCard } from '@/components/agent-chat/pasted-rows-import-card.component';
import { LearningPart } from '@/components/agent-chat/learning-row.component';
import { ListRowsLink } from '@/components/agent-chat/list-rows-link.component';
import {
  isMemoryTool,
  LEARNING_DISPLAY,
} from '@/components/agent-chat/learning-row.util';
import { PlanRequiredCard } from '@/components/agent-chat/plan-required-card.component';
import { AirtableIcon } from '@/components/ui/icons/airtable-icon';
import {
  GoogleDocsIcon,
  GoogleSheetsIcon,
} from '@/components/ui/icons/google-product-icons';
import { testRunOf } from '@/components/agent-chat/agent-cards.util';
import {
  flagsLine,
  keptLine,
  MERGE_DUPLICATES_ACTION,
  MERGE_STARTED_DISPLAY,
  mergeCardOf,
  mergeObjectsLine,
} from '@/components/agent-chat/duplicate-cards.util';
import { QuestionCardPart } from '@/components/agent-chat/question-card.component';
import {
  GumloopResultCard,
  PeopleResultCard,
  RESULT_CARD_LINK,
  ResultCard,
  ResultNoticeCard,
} from '@/components/agent-chat/result-cards.component';
import {
  QUESTION_CARD_DISPLAY,
  type ThreadPartLike,
} from '@/components/agent-chat/question-card.util';
import { stepDetailLines } from '@/components/agent-chat/steps.util';
import { resultStepLabel } from '@/components/agent-chat/tool-labels';
import { asRecord } from '@/components/agent-chat/as-record.util';
import { useAgentDisplayEffects } from '@/components/agent-chat/use-agent-display-effects.hook';
import {
  DraftAgentCard,
  OneOffConfirmationCard,
  OneOffRunCard,
  RepeatAgentCard,
} from '@/components/agent-chat/one-off/one-off-cards.component';
import {
  AGENT_RUN_STARTED,
  DRAFT_AGENT_CREATED,
  hasDraftAgentPart,
  ONE_OFF_ACTIONS,
  ONE_OFF_RUN_STARTED,
  REPEAT_AGENT_CREATED,
} from '@/components/agent-chat/one-off/one-off.util';
import { useCardBalance } from '@/components/agent-chat/use-buy-credits.hook';
import { ImportCountField } from '@/components/agent-chat/import-count-field.component';
import {
  INSTANTLY_ACTIONS,
  isInstantlyRemove,
} from '@/components/agent-chat/clu-tool-steps';
import { useInstantlyEnabled } from '@/hooks/use-instantly-enabled.hook';
import { ListShapingConfirmationCard } from '@/components/agent-chat/list-shaping/list-shaping-card.component';
import { LIST_SHAPING_ACTIONS } from '@/components/agent-chat/list-shaping/list-shaping.util';
import { useCopilotPane } from '@/components/copilot/copilot-pane-context';
import {
  groupName,
  mergeBlockedReason,
  mergeConfirm,
} from '@/containers/agents/duplicate-review/duplicate-review.util';
import {
  type CluCompanyHandoff,
  cluCompanyHandoff,
  cluPeopleHandoff,
  type CluPeopleHandoff,
  searchDidNotRun,
} from '@/containers/search/clu-criteria-handoff';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useCluMemoryEnabled } from '@/hooks/use-clu-memory-flag.hook';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  type ConfirmationRequest,
  type ConfirmationIdentity,
  type TryConfirmationIdentity,
} from '@/hooks/use-agent-chat.hook';
import {
  isEnrichmentMessage,
  useEnrichmentWebSocket,
} from '@/hooks/use-unified-websocket.hook';
import { ImportCapNotice } from '@/components/search/table/import-cap-notice.component';
import { importCap } from '@/components/search/table/import-cap.util';
import { cn } from '@/lib/utils';
import { parseServerDate } from '@/lib/parse-server-date';
import { useAppDispatch, useAppSelector } from '@/store';
import { useGetOrCreateDefaultLeadListMutation } from '@/store/features/lead-lists.feature';
import { useEnrichProspectManuallyMutation } from '@/store/features/enrichment.feature';
import { useGetAgentQuery } from '@/store/features/gtm-agents.feature';
import {
  selectAiChatContext,
  setAiChatOpen,
} from '@/store/features/ui.feature';
import { skipToken } from '@reduxjs/toolkit/query';
import type { ManualProspectEnrichmentRequest } from '@/types';
import { INITIAL_FILTERS } from '@/types/search-filters';
import {
  hasRetiredFunctions,
  crmExclusionsOnlyTheChatApplies,
  mapAgentCompanySearchParamsToCompanyFilters,
  mapAgentSearchParamsToFilters,
  mapAgentSearchParamsToCompanyFilters,
  RETIRED_FUNCTIONS_NOTICE,
} from '@/utils/agent-filters-to-search-filters';
import { INITIAL_COMPANY_FILTERS } from '@/utils/company-search.utils';
import { encodeSearchQuery } from '@/utils/search-query-encoding';
import {
  makeAssistantToolUI,
  useAuiState,
  type ToolCallMessagePartProps,
} from '@assistant-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { isOptedOutFailure, OPTED_OUT_MESSAGE } from '@/utils/opted-out';
import { toastIfOptedOut } from '@/utils/opted-out-toast';
import { useCreditGate } from '@/hooks/use-credit-gate.hook';
import {
  iconArrowRight,
  iconArrowUpRightFromSquare,
  iconArrowsLeftRight,
  iconBan,
  iconBrain,
  iconBuilding,
  iconBuildings,
  iconCheck,
  iconChevronDown,
  iconCircleCheck,
  iconCopy,
  iconEnvelope,
  iconEnvelopeCircleCheck,
  iconFileArrowUp,
  iconFilter,
  iconFlask,
  iconFolderPlus,
  iconLayerGroup,
  iconLinkedin,
  iconListCheck,
  iconPaperPlane,
  iconPause,
  iconPen,
  iconPhone,
  iconPlay,
  iconPlug,
  iconReceipt,
  iconReply,
  iconRocket,
  iconSlack,
  iconSliders,
  iconTable,
  iconTableColumns,
  iconToggleOn,
  iconTrash,
  iconTriangleExclamation,
  iconUserMagnifyingGlass,
  iconUserPlus,
  iconUsers,
  iconWallet,
  iconWandMagicSparkles,
  iconWavePulse,
  iconWebhook,
} from '@/lib/icons';
import {
  SEND_MAILBOX_EMAIL_ACTION,
  SendEmailPreview,
  useEmailEdits,
} from './send-email-preview.component';
import {
  REPLY_IN_THREAD_ACTION,
  ReplyInThreadPreview,
} from './reply-in-thread-preview.component';
import {
  confirmationDescription,
  SEND_SLACK_MESSAGE_ACTION,
  SendSlackMessagePreview,
} from './send-slack-message-preview.component';
import {
  SEND_LINKEDIN_ACTION,
  SendLinkedinPreview,
} from './send-linkedin-preview.component';
import {
  CONNECTION_API_WRITE_ACTION,
  ConnectionApiWritePreview,
} from './connection-api-write-preview.component';

type IconShim = ComponentType<{ className?: string }>;
const AlertTriangle: IconShim = (props) => (
  <AppIcon icon={iconTriangleExclamation} {...props} />
);
const Check: IconShim = (props) => <AppIcon icon={iconCheck} {...props} />;
const Copy: IconShim = (props) => <AppIcon icon={iconCopy} {...props} />;
const ExternalLink: IconShim = (props) => (
  <AppIcon icon={iconArrowUpRightFromSquare} {...props} />
);
const Filter: IconShim = (props) => <AppIcon icon={iconFilter} {...props} />;
const FolderPlus: IconShim = (props) => (
  <AppIcon icon={iconFolderPlus} {...props} />
);
const Mail: IconShim = (props) => <AppIcon icon={iconEnvelope} {...props} />;
const Phone: IconShim = (props) => <AppIcon icon={iconPhone} {...props} />;
const Trash2: IconShim = (props) => <AppIcon icon={iconTrash} {...props} />;
const Users: IconShim = (props) => <AppIcon icon={iconUsers} {...props} />;
const Buildings: IconShim = (props) => (
  <AppIcon icon={iconBuildings} {...props} />
);
const Wand2: IconShim = (props) => (
  <AppIcon icon={iconWandMagicSparkles} {...props} />
);

type ListItem = { id: string; name: string };

/**
 * Result of confirming an action. `ok: false` (without `aborted`) means the
 * billable confirm failed to execute — the confirmation surface re-enables its
 * Confirm button so the user can retry. Returned by `useAgentChat.confirmAction`.
 */
type ConfirmResult = { ok: boolean; aborted?: boolean } | void;

type CopilotToolUiContextValue = {
  onConfirm: (
    selection?: Record<string, unknown>,
    expected?: TryConfirmationIdentity | ConfirmationIdentity
  ) => void | Promise<ConfirmResult>;
  onReject: () => void;
  pendingConfirmationId: string | null;
  pendingConfirmationActionToken?: string | null;
  lists: ListItem[];
  /**
   * Destination list for card-driven actions (Reveal / Add to list) when
   * the user hasn't picked a specific list. Resolved upstream in the
   * order: active-page list → protected "Extension Leads" → first list.
   */
  defaultListId: string | null;
};

type ToolResultArgs = {
  id: string;
  name: string;
  displayType?: string;
  inputArgs?: Record<string, unknown>;
};

export type ToolResultPayload = Record<string, unknown> & {
  __toolResultId?: string;
  __toolName?: string;
  __displayType?: string;
  /** The tool result's whole display payload (a card's own fields). */
  __display?: Record<string, unknown>;
  /** Set by use-agent-chat when a tool result is replayed from persisted
   *  history (not a live stream this turn). Side-effectful surfaces such as
   *  PeopleSearchHandoffSurface read it to suppress auto-navigation so
   *  reopening or reloading the chat never yanks the user to another page. */
  __fromHistory?: boolean;
};

const CopilotToolUiContext = createContext<CopilotToolUiContextValue | null>(
  null
);

/**
 * Per-lead enrichment add-on cost. Import itself is free, so the quoted
 * total is just enrichment cost × rows.
 */
const ENRICHMENT_OPTIONS = [
  { id: 'none', label: 'No enrichment', cost: 0 },
  { id: 'partial', label: 'Email', cost: 1 },
  { id: 'full', label: 'Email and phone', cost: 11 },
] as const;

/** The most leads Clu found to import: the field's ceiling and its default.
 *  Null when the server stored no count: it then refuses any count sent, so
 *  the card shows no count field and sends none. */
const importMaxCount = (request: ConfirmationRequest): number | null => {
  const count = request.arguments?.count;
  if (typeof count !== 'number' || !Number.isFinite(count)) return null;
  return Math.max(1, Math.floor(count));
};

const useCopilotToolUiContext = () => {
  const context = useContext(CopilotToolUiContext);
  if (!context) {
    throw new Error('Copilot tool UIs must be rendered inside CopilotToolUIs.');
  }
  return context;
};

const toNumber = (value: unknown, fallback = 0) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

/** Returns seconds remaining until expiry, or null if no expiry. Returns 0 when expired. */
const useCountdown = (expiresAt?: string) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(() => {
    if (!expiresAt) return null;
    const diff = Math.max(
      0,
      Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)
    );
    return Number.isNaN(diff) ? null : diff;
  });

  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const expiry = new Date(expiresAt).getTime();
    if (Number.isNaN(expiry)) {
      setSecondsLeft(null);
      return;
    }
    if (expiry <= Date.now()) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return secondsLeft;
};

/** A card that waits until the user answers carries an expiry years away:
 *  it shows no timer. Only a card that can run out within a day counts down. */
const COUNTDOWN_SHOWN_FROM_SECONDS = 24 * 60 * 60;

const CountdownBadge = ({ secondsLeft }: { secondsLeft: number }) => {
  if (secondsLeft > COUNTDOWN_SHOWN_FROM_SECONDS) return null;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const isUrgent = secondsLeft < 60;

  if (secondsLeft <= 0) return <CardStatus tone='danger'>Expired</CardStatus>;

  return (
    <span
      className={cn(
        'text-[12px] leading-[16px] whitespace-nowrap tabular-nums',
        isUrgent ? 'font-medium text-destructive' : 'text-muted-foreground'
      )}
    >
      {minutes}:{seconds.toString().padStart(2, '0')} remaining
    </span>
  );
};

/** Minimal inline-markdown for tool-card titles: backend emits `**bold**`
 * (and occasionally `[label](url)`) inside short status strings. The
 * Thread's MarkdownText handles long-form messages but the small
 * ResultNotice card was rendering these raw — visible `**` asterisks.
 * Kept inline (no remark/rehype) since the surface area is one line. */
const renderInlineMarkdown = (input: string): ReactNode => {
  const out: ReactNode[] = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(input)) !== null) {
    if (match.index > lastIndex) {
      out.push(input.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      out.push(<strong key={`b${key++}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined && match[3] !== undefined) {
      out.push(
        <a
          key={`a${key++}`}
          href={match[3]}
          target='_blank'
          rel='noreferrer'
          className='text-primary hover:underline'
        >
          {match[2]}
        </a>
      );
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < input.length) out.push(input.slice(lastIndex));
  return out.length === 0 ? input : out;
};

type ResultNoticeProps = {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  trailing?: ReactNode;
  variant?: 'success' | 'error' | 'neutral';
};

/** A notice in the agents look: the agent card's shell, title and action. */
const ResultNotice = ({ icon, ...rest }: ResultNoticeProps) => (
  <ResultNoticeCard Icon={icon} {...rest} />
);

type PersonData = {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  title: string;
  company: string;
  location: string;
  linkedin_url: string;
  company_linkedin_url: string;
  company_domain: string;
  email: string;
  phone: string;
};

/** Generate initials from a name for the avatar. */
const getInitials = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Generate a masked email like p****@stripe.com */
const getMaskedEmail = (person: PersonData) => {
  if (person.email) return person.email;
  const initial = (person.firstName || person.name || 'u')[0].toLowerCase();
  const domain =
    person.company_domain ||
    (person.company
      ? person.company.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com'
      : 'company.com');
  return `${initial}****@${domain}`;
};

/** Truncate a URL for display: linkedin.com/in/patrickcollison */
const displayUrl = (url: string) => {
  try {
    return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  } catch {
    return url;
  }
};

/** Returns a safe http(s) href or null if the URL is invalid/unsafe. */
const safeHref = (
  url: string | undefined | null,
  defaultScheme?: string
): string | null => {
  if (!url) return null;
  try {
    const normalized =
      defaultScheme && !/^https?:\/\//i.test(url)
        ? `${defaultScheme}${url}`
        : url;
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

/** Contact row with blur + reveal hover + credit cost */
const ContactRow = ({
  icon: Icon,
  value,
  isRevealed,
  isRevealing,
  creditCost,
  onReveal,
}: {
  icon: ComponentType<{ className?: string }>;
  value: string;
  isRevealed: boolean;
  isRevealing?: boolean;
  creditCost: number;
  onReveal: () => void;
}) => (
  <button
    type='button'
    onClick={isRevealed || isRevealing ? undefined : onReveal}
    disabled={isRevealed || isRevealing}
    className={cn(
      'group/contact flex w-full items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] text-left text-[12px] leading-[16px] transition-colors',
      !isRevealed && !isRevealing && 'cursor-pointer hover:bg-muted/60'
    )}
  >
    <Icon className='h-3 w-3 shrink-0 text-muted-foreground' />
    <div className='relative min-w-0 flex-1'>
      <span
        className={cn(
          'block truncate transition-all',
          isRevealed
            ? 'text-foreground'
            : isRevealing
              ? 'text-muted-foreground select-none'
              : 'text-muted-foreground blur-[2px] select-none group-hover/contact:opacity-0'
        )}
      >
        {isRevealing ? 'Revealing\u2026' : value}
      </span>
      {!isRevealed && !isRevealing ? (
        <span className='absolute inset-0 flex items-center font-medium text-foreground opacity-0 transition-opacity group-hover/contact:opacity-100'>
          Reveal
        </span>
      ) : null}
    </div>
    {!isRevealed && !isRevealing ? (
      <CreditAmount amount={creditCost} className='text-muted-foreground' />
    ) : null}
  </button>
);

const PersonCard = ({ person }: { person: PersonData }) => {
  const { lists, defaultListId: contextDefaultListId } =
    useCopilotToolUiContext();
  const [listPickerOpen, setListPickerOpen] = useState(false);

  // Reveal directly runs a manual enrichment against the user's first
  // available list, and the card listens to the enrichment websocket for the
  // matching task_id so the result appears in-place without a chat round-trip.
  const [revealedEmail, setRevealedEmail] = useState<string | null>(null);
  const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
  const [revealingEmail, setRevealingEmail] = useState(false);
  const { openCreditGate } = useCreditGate();
  const [revealingPhone, setRevealingPhone] = useState(false);
  const emailTaskIdRef = useRef<string | null>(null);
  const phoneTaskIdRef = useRef<string | null>(null);

  const [enrichManually] = useEnrichProspectManuallyMutation();

  const hasEmail = Boolean(person.email) || Boolean(revealedEmail);
  const hasPhone = Boolean(person.phone) || Boolean(revealedPhone);
  const emailValue = revealedEmail || person.email;
  const phoneValue = revealedPhone || person.phone;

  // Upstream resolves this as context-list → protected Extension Leads.
  // Orgs onboarded before the protected-list webhook have NO such list —
  // resolveListId below fails open by asking the backend to get-or-create
  // it (idempotent) instead of dead-ending the reveal.
  const defaultListId = contextDefaultListId;
  const [getOrCreateDefaultList] = useGetOrCreateDefaultLeadListMutation();

  const resolveListId = useCallback(async (): Promise<string | null> => {
    if (defaultListId) return defaultListId;
    try {
      const created = await getOrCreateDefaultList().unwrap();
      return created.id;
    } catch {
      return null;
    }
  }, [defaultListId, getOrCreateDefaultList]);

  const buildPayload = useCallback(
    (
      listId: string,
      type: 'partial' | 'phone_only'
    ): ManualProspectEnrichmentRequest => {
      const [first, last] = splitPersonName(
        person.name,
        person.firstName,
        person.lastName
      );
      // Backend's company_domain accepts plain company names too, so fall
      // back to the visible company string when no domain is available.
      return {
        enrichment_type: type,
        linkedin_url: person.linkedin_url || undefined,
        first_name: first || undefined,
        last_name: last || undefined,
        company_domain: person.company_domain || person.company || undefined,
        lead_list_id: listId,
      };
    },
    [
      person.firstName,
      person.lastName,
      person.name,
      person.linkedin_url,
      person.company_domain,
      person.company,
    ]
  );

  const extractApiError = (err: unknown, fallback: string): string => {
    if (typeof err === 'object' && err && 'data' in err) {
      const data = (err as { data?: { detail?: string } }).data;
      if (data?.detail) return data.detail;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  };

  // Out of credits is a 402: offer the top-up, not a dead-end toast.
  const isOutOfCredits = (err: unknown): boolean =>
    typeof err === 'object' &&
    err !== null &&
    (err as { status?: unknown }).status === 402;

  // Let the backend try as long as we have *some* identifying signal -
  // name + company is enough for providers to attempt a match even when
  // there's no LinkedIn URL or explicit domain on the card.
  const hasUsableLookupKey = Boolean(
    person.linkedin_url ||
    person.company_domain ||
    (person.name && person.company)
  );

  const handleFindEmail = useCallback(async () => {
    if (!hasUsableLookupKey) {
      toast.error(
        'Need a LinkedIn URL or company domain to resolve this contact.'
      );
      return;
    }
    const listId = await resolveListId();
    if (!listId) {
      toast.error('Create or select a list first to enrich this contact.');
      return;
    }
    setRevealingEmail(true);
    try {
      const result = await enrichManually(
        buildPayload(listId, 'partial')
      ).unwrap();
      emailTaskIdRef.current = result.task_id;
    } catch (err) {
      setRevealingEmail(false);
      if (toastIfOptedOut(err)) return;
      if (isOutOfCredits(err)) {
        openCreditGate({ needed: 1, action: 'enrich-email' });
      } else {
        toast.error(extractApiError(err, 'Could not start email lookup.'));
      }
    }
  }, [
    buildPayload,
    resolveListId,
    enrichManually,
    hasUsableLookupKey,
    openCreditGate,
  ]);

  const handleFindPhone = useCallback(async () => {
    if (!hasUsableLookupKey) {
      toast.error(
        'Need a LinkedIn URL or company domain to resolve this contact.'
      );
      return;
    }
    const listId = await resolveListId();
    if (!listId) {
      toast.error('Create or select a list first to enrich this contact.');
      return;
    }
    setRevealingPhone(true);
    try {
      const result = await enrichManually(
        buildPayload(listId, 'phone_only')
      ).unwrap();
      phoneTaskIdRef.current = result.task_id;
    } catch (err) {
      setRevealingPhone(false);
      if (toastIfOptedOut(err)) return;
      if (isOutOfCredits(err)) {
        openCreditGate({ needed: 10, action: 'enrich-phone' });
      } else {
        toast.error(extractApiError(err, 'Could not start phone lookup.'));
      }
    }
  }, [
    buildPayload,
    resolveListId,
    enrichManually,
    hasUsableLookupKey,
    openCreditGate,
  ]);

  // Listen for enrichment completion for our task_ids and hydrate the card.
  useEnrichmentWebSocket((msg) => {
    if (!isEnrichmentMessage(msg) || !msg.task_id) return;
    const matchedEmail = msg.task_id === emailTaskIdRef.current;
    const matchedPhone = msg.task_id === phoneTaskIdRef.current;
    if (!matchedEmail && !matchedPhone) return;
    if (msg.status === 'completed') {
      if (matchedEmail) {
        setRevealedEmail(msg.result?.email ?? '-');
        setRevealingEmail(false);
        emailTaskIdRef.current = null;
      }
      if (matchedPhone) {
        setRevealedPhone(msg.result?.phone ?? '-');
        setRevealingPhone(false);
        phoneTaskIdRef.current = null;
      }
    } else if (msg.status === 'failed') {
      if (matchedEmail) {
        setRevealingEmail(false);
        emailTaskIdRef.current = null;
      }
      if (matchedPhone) {
        setRevealingPhone(false);
        phoneTaskIdRef.current = null;
      }
      if (isOptedOutFailure(msg)) toast.info(OPTED_OUT_MESSAGE);
      else toast.error(msg.error || msg.message || 'Enrichment failed.');
    }
  });

  const handleAddToList = useCallback(
    async (list: ListItem) => {
      setListPickerOpen(false);
      if (!hasUsableLookupKey) {
        toast.error(
          'Need a LinkedIn URL or company domain to add this contact.'
        );
        return;
      }
      try {
        await enrichManually({
          ...buildPayload(list.id, 'partial'),
          enrichment_type: 'none',
        }).unwrap();
        toast.success(`Added ${person.name || 'contact'} to ${list.name}`);
      } catch (err) {
        toast.error(extractApiError(err, 'Could not add to list.'));
      }
    },
    [buildPayload, enrichManually, hasUsableLookupKey, person.name]
  );

  const linkedinDisplay = person.linkedin_url
    ? displayUrl(person.linkedin_url)
    : null;
  const companyLinkedinDisplay = person.company_linkedin_url
    ? displayUrl(person.company_linkedin_url)
    : null;
  const companyWebsite = person.company_domain || null;

  return (
    <div className='flex flex-col gap-[6px] py-[10px] first:pt-0 last:pb-0'>
      <div className='flex items-start gap-[8px]'>
        <div className='flex size-[24px] shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground'>
          {getInitials(person.name)}
        </div>
        <div className='min-w-0 flex-1 text-[12px] leading-[16px]'>
          <p className='truncate font-medium text-foreground'>
            {person.name || 'Unknown'}
          </p>
          {person.title ? (
            <p className='truncate text-muted-foreground'>{person.title}</p>
          ) : null}
          {person.company || person.location ? (
            <p className='truncate text-muted-foreground'>
              {[person.company, person.location]
                .filter(Boolean)
                .join(' \u00b7 ')}
            </p>
          ) : null}
        </div>
      </div>

      {linkedinDisplay || companyLinkedinDisplay || companyWebsite ? (
        <div className='flex flex-col gap-[2px] text-[12px] leading-[16px]'>
          {linkedinDisplay && safeHref(person.linkedin_url) ? (
            <a
              href={safeHref(person.linkedin_url)!}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-[8px] text-muted-foreground transition-colors hover:text-foreground'
            >
              <AppIcon icon={iconLinkedin} className='h-3 w-3 shrink-0' />
              <span className='truncate'>{linkedinDisplay}</span>
            </a>
          ) : null}
          {companyLinkedinDisplay && safeHref(person.company_linkedin_url) ? (
            <a
              href={safeHref(person.company_linkedin_url)!}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-[8px] text-muted-foreground transition-colors hover:text-foreground'
            >
              <AppIcon icon={iconLinkedin} className='h-3 w-3 shrink-0' />
              <span className='truncate'>{companyLinkedinDisplay}</span>
            </a>
          ) : null}
          {companyWebsite && safeHref(companyWebsite, 'https://') ? (
            <a
              href={safeHref(companyWebsite, 'https://')!}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-[8px] text-muted-foreground transition-colors hover:text-foreground'
            >
              <ExternalLink className='h-3 w-3 shrink-0' />
              <span className='truncate'>{companyWebsite}</span>
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Contact: email + phone with reveal + credit cost */}
      <div className='-mx-[6px]'>
        <ContactRow
          icon={Mail}
          value={emailValue || getMaskedEmail(person)}
          isRevealed={hasEmail}
          isRevealing={revealingEmail}
          creditCost={1}
          onReveal={handleFindEmail}
        />
        <ContactRow
          icon={Phone}
          value={phoneValue || '+* (***) ***-****'}
          isRevealed={hasPhone}
          isRevealing={revealingPhone}
          creditCost={10}
          onReveal={handleFindPhone}
        />
      </div>

      <Popover open={listPickerOpen} onOpenChange={setListPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            className={cn(
              CARD_LINK,
              'inline-flex items-center gap-[4px] self-start'
            )}
          >
            Add to list
            <AppIcon icon={iconChevronDown} className='h-3 w-3' />
          </button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-72 p-1' sideOffset={4}>
          <div className='max-h-48 overflow-y-auto'>
            {lists.length > 0 ? (
              lists.map((list) => (
                <button
                  key={list.id}
                  type='button'
                  onClick={() => handleAddToList(list)}
                  className='flex w-full items-center gap-[8px] rounded-[6px] px-[10px] py-[6px] text-left text-[12px] leading-[16px] text-foreground transition-colors hover:bg-muted/60'
                >
                  <AppIcon
                    icon={iconTable}
                    className='h-3 w-3 shrink-0 text-muted-foreground'
                  />
                  <span className='truncate'>{list.name}</span>
                </button>
              ))
            ) : (
              <div className='px-[10px] py-[8px] text-[12px] leading-[16px] text-muted-foreground'>
                No lists yet
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const str = (row: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    if (typeof row[key] === 'string') return row[key] as string;
  }
  return '';
};

/** Parse raw person rows into PersonData array. */
const parsePeople = (result: ToolResultPayload): PersonData[] => {
  const rows = Array.isArray(result.results) ? result.results : [];
  return rows.map((person, index) => {
    const row =
      typeof person === 'object' && person
        ? (person as Record<string, unknown>)
        : {};
    const firstName = str(row, 'first_name');
    const lastName = str(row, 'last_name');
    return {
      id: String(
        row.linkedin_url || `${row.name || `${firstName}-${lastName}`}-${index}`
      ),
      firstName,
      lastName,
      name: str(row, 'full_name', 'name') || `${firstName} ${lastName}`.trim(),
      title: str(row, 'title', 'headline', 'job_title'),
      company: str(row, 'company_name', 'company'),
      location: str(row, 'location'),
      linkedin_url: str(
        row,
        'linkedin_url',
        'flagship_profile_url',
        'profile_url',
        'linkedin'
      ),
      company_linkedin_url: str(
        row,
        'company_linkedin_url',
        'company_linkedin'
      ),
      company_domain: str(
        row,
        'company_domain',
        'domain',
        'company_website',
        'website'
      ),
      email: str(row, 'email'),
      phone: str(row, 'phone'),
    };
  });
};

/** A search the tool already ran and answered with its rows (a Slack-style
 *  result): there is nothing to hand to the page. The result says so itself
 *  (`executed`); without that, rows in it do. */
const searchAlreadyRan = (result: ToolResultPayload): boolean =>
  typeof result.executed === 'boolean'
    ? result.executed
    : parsePeople(result).length > 0;

/** Rich person cards for web_people_search (1-3 targeted results). */
export const PeopleCardsSurface = ({
  result,
}: {
  result: ToolResultPayload;
}) => {
  const people = parsePeople(result);

  if (people.length === 0) {
    return <ResultNotice icon={Users} title='No people found' />;
  }

  // One block, a line between people: the result card is the only border.
  return (
    <div className='flex flex-col divide-y divide-border'>
      {people.map((person) => (
        <PersonCard key={person.id} person={person} />
      ))}
    </div>
  );
};

/** People already in a list, or a search nothing on the page could take. */
export const PeopleTableSurface = ({
  result,
}: {
  result: ToolResultPayload;
}) => {
  const people = parsePeople(result);
  const total = toNumber(result.total, people.length);

  if (people.length === 0) {
    return <ResultNotice icon={Users} title='No people found' />;
  }
  return <PeopleResultCard people={people} total={total} />;
};

/** The credit balance: one line, the coin and the number at the right. The
 *  agents chat shows it as a step (CARD_DISPLAYS); the old thread here. */
const CreditBalanceSurface = ({ result }: { result: ToolResultPayload }) => (
  <ResultCard
    icon={iconWallet}
    title='Credits'
    trailing={
      <span className='flex items-center gap-[4px] pr-[4px]'>
        <CardCost amount={toNumber(result.credits_available)} />
        <span className='text-[12px] leading-[16px] text-muted-foreground'>
          left
        </span>
      </span>
    }
  />
);

/** What an estimate_cost call priced, by its `operation`. */
const ESTIMATE_WORDS: Readonly<Record<string, string>> = {
  import: 'import',
  email_enrichment: 'email enrichment',
  phone_enrichment: 'phone enrichment',
  full_enrichment: 'email and phone enrichment',
  smart_column: 'smart column',
};

/**
 * A cost estimate: one line, what it priced and "About" the total at the
 * right, amber when the balance can't cover it. The agents chat folds it
 * into the steps line (CARD_DISPLAYS); the old thread shows it here.
 */
const CostEstimateSurface = ({ result }: { result: ToolResultPayload }) => {
  const operation =
    typeof result.operation === 'string' ? result.operation : '';
  const count = toNumber(result.count);
  const what = ESTIMATE_WORDS[operation] ?? 'this action';
  return (
    <ResultCard
      icon={iconReceipt}
      title={
        count > 0
          ? `Estimate for ${what} on ${count.toLocaleString()} ${
              count === 1 ? 'lead' : 'leads'
            }`
          : `Estimate for ${what}`
      }
      trailing={
        <span className='pr-[4px]'>
          <CardCost
            amount={toNumber(result.total_cost)}
            about
            short={result.sufficient_credits === false}
          />
        </span>
      }
    />
  );
};

const ApplyFiltersSurface = ({ result }: { result: ToolResultPayload }) => {
  let filterCount = 0;
  if (typeof result.filter_count === 'number') {
    filterCount = result.filter_count;
  } else if (Array.isArray(result.filters)) {
    filterCount = result.filters.length;
  }

  return (
    <ResultNotice
      icon={Filter}
      title={
        filterCount > 0
          ? `Applied ${filterCount} filter${filterCount === 1 ? '' : 's'}`
          : 'Cleared filters'
      }
    />
  );
};

const ListResultSurface = ({
  icon: Icon,
  title,
  listId,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  listId?: string;
}) => (
  <ResultNoticeCard
    Icon={Icon}
    title={title}
    action={
      listId ? (
        <ListRowsLink listId={listId} className={RESULT_CARD_LINK}>
          Open list
        </ListRowsLink>
      ) : undefined
    }
  />
);

const openLiveChat = () => {
  window.Intercom?.('show');
};

/**
 * Tracks paths we've already auto-navigated to in this browser session.
 * Prevents double-navigation when React re-renders the surface but lets the
 * "Go again" button still work for explicit user clicks.
 */
const NAVIGATED_PATHS_KEY = '__copilot_navigated_paths__';

const hasAlreadyNavigated = (path: string): boolean => {
  if (typeof window === 'undefined') return false;
  const store = (window as unknown as Record<string, Set<string>>)[
    NAVIGATED_PATHS_KEY
  ];
  return store ? store.has(path) : false;
};

const markNavigated = (path: string) => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as Record<string, Set<string>>;
  if (!w[NAVIGATED_PATHS_KEY]) w[NAVIGATED_PATHS_KEY] = new Set<string>();
  w[NAVIGATED_PATHS_KEY].add(path);
};

/**
 * Split an agent-provided path (which may carry a query string, e.g.
 * "/search?tab=people") into TanStack Router navigate args. TanStack matches
 * `to` against the pathname ONLY and takes search params as a separate object -
 * a query string crammed into `to` won't match the route and renders a blank
 * page. This is why "go to people search" was landing on a blank screen: the
 * agent emitted "/search?tab=people" and `navigate({ to: rawPath })` failed to
 * match. Parsing the query into `search` fixes it for any path with a query.
 */
const toNavigateArgs = (
  rawPath: string
): { to: string; search?: Record<string, string> } => {
  const qIndex = rawPath.indexOf('?');
  if (qIndex === -1) return { to: rawPath };
  const to = rawPath.slice(0, qIndex);
  const search: Record<string, string> = {};
  new URLSearchParams(rawPath.slice(qIndex + 1)).forEach((value, key) => {
    search[key] = value;
  });
  return { to, search };
};

const NavigateResultSurface = ({ path }: { path: string }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const isLiveChat = path === '__chatwoot';
  const hasFiredRef = useRef(false);
  // A draft agent's card in the same message opens that agent: this page
  // would only pull the user off it, and its card adds nothing.
  const afterDraftAgent = useAuiState((s) =>
    hasDraftAgentPart(s.message.content as readonly ThreadPartLike[])
  );

  useEffect(() => {
    // Auto-navigate exactly once per (component instance, path).
    // Skip if a previous render of this same path already navigated.
    if (hasFiredRef.current || afterDraftAgent) return;
    if (hasAlreadyNavigated(path)) return;
    hasFiredRef.current = true;
    markNavigated(path);

    if (isLiveChat) {
      openLiveChat();
    } else {
      try {
        navigate(toNavigateArgs(path));
        // Dock the Copilot so the conversation survives the route change:
        // a full-screen /clu handoff becomes page + docked sidebar,
        // thread intact. No-op if the sidebar is already open.
        dispatch(setAiChatOpen(true));
        posthog?.capture('copilot_surface_opened', {
          surface: 'docked_sidebar',
          source: 'handoff',
        });
      } catch {
        /* ignore */
      }
    }
  }, [navigate, dispatch, posthog, path, isLiveChat, afterDraftAgent]);

  if (afterDraftAgent) return null;
  if (isLiveChat) {
    return (
      <ResultNotice
        icon={Users}
        title='Live chat opened'
        description={
          <button
            type='button'
            className='inline text-xs font-medium text-primary hover:underline'
            onClick={openLiveChat}
          >
            Reopen chat &rarr;
          </button>
        }
      />
    );
  }

  // Show a friendly label instead of raw UUID paths
  let friendlyLabel: string;
  if (path.startsWith('/lists/')) {
    friendlyLabel = 'Opened list';
  } else if (path === '/plans') {
    friendlyLabel = 'Opened plans';
  } else if (path === '/settings') {
    friendlyLabel = 'Opened settings';
  } else if (path.startsWith('/finder') || path.startsWith('/search')) {
    friendlyLabel = path.includes('tab=companies')
      ? 'Opened company search'
      : 'Opened people search';
  } else {
    friendlyLabel = `Opened ${path.replace(/^\//, '')}`;
  }

  // ``Go again`` CTA mirrors the auto-nav handoff side-effects above
  // (dock the Copilot + emit the same telemetry) so the conversation
  // persists across the route change. Without these, the manual click
  // silently dropped the docked sidebar.
  const handleManualNavigate = () => {
    try {
      navigate(toNavigateArgs(path));
      dispatch(setAiChatOpen(true));
      posthog?.capture('copilot_surface_opened', {
        surface: 'docked_sidebar',
        source: 'handoff',
      });
    } catch {
      /* ignore */
    }
  };

  return (
    <ResultNotice
      icon={ExternalLink}
      title={friendlyLabel}
      description={
        <button
          type='button'
          className='inline text-xs font-medium text-primary hover:underline'
          onClick={handleManualNavigate}
        >
          Go again &rarr;
        </button>
      }
    />
  );
};

/**
 * People-search handoff. When the agent's `people_search` tool returns, we
 * do NOT render the rows inline - the cramped sidebar can't sort, select,
 * enrich, or export. Instead we put the interpreted search onto the page's
 * SearchFilters, encode it into the `/search` page's existing `q` param, and
 * navigate there. The page's q-hydration applies the filters and runs the
 * search natively in the main table; the Copilot stays open as a
 * conversational layer.
 *
 * Where the filters come from (CLE-550):
 *   - a result with `criteria` carries the API's server-validated, camelCase
 *     page filters. They are loaded as they are (see clu-criteria-handoff). One
 *     that does not fit the page's vocabulary opens nothing and says so on the
 *     card, never a search with a value left out.
 *   - a result without them is an older one: the tool's raw `input_args` go
 *     through the legacy mapper, as before.
 *
 * Auto-navigate discipline mirrors NavigateResultSurface:
 *   - hasFiredRef guards re-renders of THIS instance,
 *   - the global navigated-set guards re-mounts (chat scroll / virtualization)
 *     of the SAME result, keyed by the unique tool-result id,
 *   - `__fromHistory` (tagged by use-agent-chat on history load) blocks
 *     auto-nav for replayed results, so reopening or reloading the chat never
 *     yanks the user to /search. Same precedent as the progress surfaces,
 *     which use-agent-chat force-completes on history load.
 *
 * Falls back to the inline PeopleTableSurface when there is nothing to hand
 * off (no mappable filters, criteria that change nothing) or the tool already
 * ran the search and returned its rows.
 */
export const PeopleSearchHandoffSurface = ({
  result,
  inputArgs,
}: {
  result: ToolResultPayload;
  inputArgs?: Record<string, unknown>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const total = toNumber(result.total);

  // Source the interpreted NL params once. Prefer the tool's input args, fall
  // back to the `search_params` the result echoes.
  const args = useMemo(
    () => (inputArgs ?? result.search_params ?? {}) as Record<string, unknown>,
    [inputArgs, result.search_params]
  );
  // A search that did not run carries the filters it tried (for the next
  // turn to correct), never ones to open.
  const notRun = searchDidNotRun(result);
  const handoff = useMemo<CluPeopleHandoff>(
    () =>
      notRun
        ? { kind: 'invalid', error: 'the search did not run' }
        : cluPeopleHandoff(result.criteria, result.company_criteria),
    [notRun, result.criteria, result.company_criteria]
  );
  // Only an older result goes through the legacy mapper.
  const legacy = handoff.kind === 'absent';
  // A search the tool already ran (rows in the result) shows its rows here.
  const ran = !legacy && searchAlreadyRan(result);
  const mapped = useMemo(
    () => (legacy ? mapAgentSearchParamsToFilters(args) : {}),
    [legacy, args]
  );
  // Company-side firmographics (funding stage, revenue, …) → composite slice.
  const mappedCompany = useMemo(
    () => (legacy ? mapAgentSearchParamsToCompanyFilters(args) : {}),
    [legacy, args]
  );
  const refused = !ran && handoff.kind === 'invalid' ? handoff.error : null;
  // The legacy mapper drops a function the provider retired, so the page says
  // the search is wider than the one Clu ran rather than widening it silently.
  const retiredFunctions = useMemo(
    () => legacy && hasRetiredFunctions(args),
    [legacy, args]
  );

  // Encode the FULL filter set so the page's q-hydration ({...prev, ...blob})
  // overwrites every key - a clean replace, not a merge onto whatever filters
  // happened to already be on the page. Shape matches pushSearchToUrl:
  // { tab, filters }. The composite firmographic slice rides under the
  // namespaced ``__compositeCompanyFilters`` key the page extracts. UTF-8 safe
  // (encodeSearchQuery), so "São Paulo" and non-Latin text survive; a failed
  // encode yields null -> inline fallback below.
  // A CRM exclusion only the chat's search applies ("not being outreached")
  // would be lost on the page: those rows stay here, where it was applied.
  const chatOnly = useMemo(
    () => crmExclusionsOnlyTheChatApplies(args).length > 0,
    [args]
  );
  const encodedQ = useMemo(() => {
    if (ran) return null;
    if (handoff.kind === 'ready') return handoff.q;
    if (!legacy) return null; // criteria that change nothing, or none valid
    if (
      chatOnly ||
      (Object.keys(mapped).length === 0 &&
        Object.keys(mappedCompany).length === 0)
    )
      return null;
    try {
      const filters: Record<string, unknown> = {
        ...INITIAL_FILTERS,
        ...mapped,
      };
      if (Object.keys(mappedCompany).length > 0) {
        filters.__compositeCompanyFilters = mappedCompany;
      }
      return encodeSearchQuery({ tab: 'people', filters });
    } catch {
      return null;
    }
  }, [ran, handoff, legacy, chatOnly, mapped, mappedCompany]);

  // Prefer the unique tool-result id (so a genuinely new search always fires,
  // even when the interpreted filters repeat); fall back to the encoded blob.
  // Namespaced so it can't collide with NavigateResultSurface's path keys.
  const dedupeKey = `people-search-handoff:${
    (typeof result.__toolResultId === 'string' && result.__toolResultId) ||
    encodedQ ||
    ''
  }`;
  const isHistory = result.__fromHistory === true;
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (refused)
      console.warn('[clu-criteria] people search not opened:', refused);
  }, [refused]);

  useEffect(() => {
    if (!encodedQ) return; // nothing mappable -> inline fallback renders
    if (isHistory) return; // replayed from history -> never auto-navigate
    if (hasFiredRef.current) return;
    if (hasAlreadyNavigated(dedupeKey)) return;
    hasFiredRef.current = true;
    markNavigated(dedupeKey);
    try {
      if (retiredFunctions)
        toast.info(RETIRED_FUNCTIONS_NOTICE, { id: 'retired-functions' });
      navigate({ to: '/finder', search: { tab: 'people', q: encodedQ } });
      // Dock the Copilot so the conversation survives the handoff: full-screen
      // /clu becomes the search page + docked sidebar, thread intact.
      // No-op if the sidebar is already open.
      dispatch(setAiChatOpen(true));
      posthog?.capture('copilot_surface_opened', {
        surface: 'docked_sidebar',
        source: 'handoff',
      });
    } catch {
      /* ignore */
    }
  }, [
    navigate,
    dispatch,
    posthog,
    encodedQ,
    isHistory,
    dedupeKey,
    retiredFunctions,
  ]);

  // Criteria that did not validate: nothing opens, and the card says so.
  if (refused) {
    return (
      <ResultNotice
        icon={AlertTriangle}
        variant='error'
        title='Search filters could not be applied'
      />
    );
  }

  // Ambiguous query (no mappable filters) or un-encodable blob: the people
  // card, so the user still sees who the agent found.
  if (!encodedQ) {
    return <PeopleTableSurface result={result} />;
  }

  // ``View results`` CTA mirrors the auto-nav handoff side-effects above
  // (dock the Copilot + emit the same telemetry) so the manual path
  // preserves the conversation just like the auto-nav path.
  const handleManualNavigate = () => {
    try {
      if (retiredFunctions)
        toast.info(RETIRED_FUNCTIONS_NOTICE, { id: 'retired-functions' });
      navigate({ to: '/finder', search: { tab: 'people', q: encodedQ } });
      dispatch(setAiChatOpen(true));
      posthog?.capture('copilot_surface_opened', {
        surface: 'docked_sidebar',
        source: 'handoff',
      });
    } catch {
      /* ignore */
    }
  };

  return (
    <ResultNotice
      icon={Users}
      title={
        total > 0
          ? `Opened ${total.toLocaleString()} ${
              total === 1 ? 'result' : 'results'
            } on the search page`
          : 'Opened the search page'
      }
      description={
        <button
          type='button'
          className='inline text-xs font-medium text-primary hover:underline'
          onClick={handleManualNavigate}
        >
          View results &rarr;
        </button>
      }
    />
  );
};

/**
 * Companies twin of PeopleSearchHandoffSurface (CLE-401): put the agent's
 * interpreted CompanyDB search onto CompanySearchFilters, encode it into the
 * finder's `q` param and navigate. A result with `criteria` carries the API's
 * server-validated filters, loaded as they are (CLE-550); an older one goes
 * through the legacy mapper. Same auto-navigate guards.
 */
export const CompanySearchHandoffSurface = ({
  result,
  inputArgs,
}: {
  result: ToolResultPayload;
  inputArgs?: Record<string, unknown>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  const args = useMemo(
    () => (inputArgs ?? result.search_params ?? {}) as Record<string, unknown>,
    [inputArgs, result.search_params]
  );
  const notRun = searchDidNotRun(result);
  const handoff = useMemo<CluCompanyHandoff>(
    () =>
      notRun
        ? { kind: 'invalid', error: 'the search did not run' }
        : cluCompanyHandoff(result.criteria),
    [notRun, result.criteria]
  );
  const legacy = handoff.kind === 'absent';
  const mapped = useMemo(
    () => (legacy ? mapAgentCompanySearchParamsToCompanyFilters(args) : {}),
    [legacy, args]
  );
  const refused = handoff.kind === 'invalid' ? handoff.error : null;

  // Full filter set so hydration replaces rather than merges.
  const encodedQ = useMemo(() => {
    if (handoff.kind === 'ready') return handoff.q;
    if (!legacy || Object.keys(mapped).length === 0) return null;
    try {
      return encodeSearchQuery({
        tab: 'company',
        filters: { ...INITIAL_COMPANY_FILTERS, ...mapped },
      });
    } catch {
      return null;
    }
  }, [handoff, legacy, mapped]);

  const dedupeKey = `company-search-handoff:${
    (typeof result.__toolResultId === 'string' && result.__toolResultId) ||
    encodedQ ||
    ''
  }`;
  const isHistory = result.__fromHistory === true;
  const hasFiredRef = useRef(false);

  const go = useCallback(() => {
    if (!encodedQ) return;
    try {
      navigate({ to: '/finder', search: { tab: 'companies', q: encodedQ } });
      dispatch(setAiChatOpen(true));
      posthog?.capture('copilot_surface_opened', {
        surface: 'docked_sidebar',
        source: 'handoff',
        tab: 'companies',
      });
    } catch {
      /* ignore */
    }
  }, [navigate, dispatch, posthog, encodedQ]);

  useEffect(() => {
    if (refused)
      console.warn('[clu-criteria] company search not opened:', refused);
  }, [refused]);

  useEffect(() => {
    if (!encodedQ) return;
    if (isHistory) return;
    if (hasFiredRef.current) return;
    if (hasAlreadyNavigated(dedupeKey)) return;
    hasFiredRef.current = true;
    markNavigated(dedupeKey);
    go();
  }, [go, encodedQ, isHistory, dedupeKey]);

  // Criteria that did not validate: nothing opens, and the card says so.
  if (refused) {
    return (
      <ResultNotice
        icon={AlertTriangle}
        variant='error'
        title='Company filters could not be applied'
      />
    );
  }

  if (!encodedQ) {
    return (
      <ResultNotice
        icon={Buildings}
        variant='neutral'
        title='No company filters could be applied'
      />
    );
  }

  return (
    <ResultNotice
      icon={Buildings}
      title='Opened the Find Companies page with your filters'
      description={
        <button
          type='button'
          className='inline text-xs font-medium text-primary hover:underline'
          onClick={go}
        >
          View results &rarr;
        </button>
      }
    />
  );
};

/**
 * The surface for a display GumloopResultCard doesn't draw itself (its
 * `legacy`): lists, saved agents, validation, a company and the runs draw
 * there, so they have no case here.
 */
function renderToolResult(
  displayType: string,
  result: ToolResultPayload,
  listId: string | undefined,
  args: ToolResultArgs
): ReactNode {
  switch (displayType) {
    case 'people_table':
      // The people_table display is overloaded across three tools:
      //   web_people_search -> 1-3 targeted hits -> rich person cards (inline)
      //   people_search     -> NL prospecting -> hand off to the /search page
      //                        so the main table (sort / select / enrich /
      //                        export) owns the results, never the sidebar
      //   get_leads + others -> rows already in a list -> keep inline table
      if (args.name === 'web_people_search') {
        return <PeopleCardsSurface result={result} />;
      }
      if (args.name === 'people_search') {
        return (
          <PeopleSearchHandoffSurface
            result={result}
            inputArgs={args.inputArgs}
          />
        );
      }
      return <PeopleTableSurface result={result} />;
    case 'credit_balance':
      return <CreditBalanceSurface result={result} />;
    case 'cost_estimate':
      return <CostEstimateSurface result={result} />;
    case 'apply_filters':
      return <ApplyFiltersSurface result={result} />;
    case 'import_result':
      return (
        <ListResultSurface
          icon={Users}
          title={String(
            result.message || `Imported ${toNumber(result.imported)} leads`
          )}
          listId={listId}
        />
      );
    case 'list_created':
      return (
        <ListResultSurface
          icon={FolderPlus}
          title={String(
            result.message || `Created ${String(result.name || 'new list')}`
          )}
          listId={typeof result.id === 'string' ? result.id : undefined}
        />
      );
    case 'list_renamed':
      return (
        <ListResultSurface
          icon={Wand2}
          title={String(
            result.message ||
              `Renamed list to ${String(result.name || 'updated list')}`
          )}
          listId={typeof result.id === 'string' ? result.id : undefined}
        />
      );
    case 'list_deleted':
      return (
        <ResultNotice
          icon={Trash2}
          title={String(result.message || 'List deleted')}
        />
      );
    case 'leads_deleted':
      return (
        <ResultNotice
          icon={Trash2}
          title={String(
            result.message || `Deleted ${toNumber(result.deleted_count)} leads`
          )}
        />
      );
    case 'leads_copied':
      return (
        <ListResultSurface
          icon={Copy}
          title={String(
            result.message || `Copied ${toNumber(result.copied_count)} leads`
          )}
          listId={
            typeof result.target_list_id === 'string'
              ? result.target_list_id
              : undefined
          }
        />
      );
    case 'leads_moved':
      return (
        <ListResultSurface
          icon={Copy}
          title={String(
            result.message || `Moved ${toNumber(result.moved_count)} leads`
          )}
          listId={
            typeof result.target_list_id === 'string'
              ? result.target_list_id
              : undefined
          }
        />
      );
    case 'navigate': {
      const navPath = typeof result.path === 'string' ? result.path : '/';
      return <NavigateResultSurface path={navPath} />;
    }
    case 'company_table':
      // company_search -> hand off to the Find Companies page (CLE-401).
      return (
        <CompanySearchHandoffSurface
          result={result}
          inputArgs={args.inputArgs}
        />
      );
    case 'google_file_created': {
      // A Sheet export or Doc Clu created in the member's Google, or an
      // Airtable table Clu is filling in the background.
      const href = safeHref(typeof result.url === 'string' ? result.url : null);
      const isDoc = result.kind === 'doc';
      const isAirtable = result.kind === 'airtable';
      return (
        <ResultNotice
          icon={
            isAirtable
              ? AirtableIcon
              : isDoc
                ? GoogleDocsIcon
                : GoogleSheetsIcon
          }
          title={
            isAirtable
              ? `Exporting ${toNumber(result.row_count)} leads to Airtable`
              : isDoc
                ? `Created the Google Doc ${String(result.title ?? '')}`.trim()
                : `Exported ${toNumber(result.row_count)} leads to Google Sheets`
          }
          action={
            href ? (
              <a
                href={href}
                target='_blank'
                rel='noreferrer'
                className={RESULT_CARD_LINK}
              >
                {isAirtable
                  ? 'Open the table'
                  : isDoc
                    ? 'Open the doc'
                    : 'Open the sheet'}
              </a>
            ) : undefined
          }
        />
      );
    }
    case 'quick_action_started':
      // Reverse-email / prospect one-offs: the workflow self-populates a new
      // Quick Actions list. Surface the "view list" affordance so the user can
      // watch it fill in. Backend `message` carries `**bold**` markdown for
      // the search URL + target list name — render it inline so the card
      // doesn't show raw asterisks.
      return (
        <ListResultSurface
          icon={Wand2}
          title={renderInlineMarkdown(
            String(result.message || 'Quick action started')
          )}
          listId={
            typeof result.list_id === 'string' ? result.list_id : undefined
          }
        />
      );
    case 'agent_test_started': {
      const run = testRunOf(result.__display, result);
      return run ? (
        <p className='text-xs text-muted-foreground'>
          <OpenRunLink runId={run.runId} agentId={run.agentId} />
        </p>
      ) : null;
    }
    case 'agent_updated':
    case 'skill_created':
    case 'skill_updated':
    case 'agent_status_changed': {
      const lines = stepDetailLines(args.name, result, result.__display);
      return lines.length ? (
        <p className='text-xs text-muted-foreground'>{lines.join(' · ')}</p>
      ) : null;
    }
    default: {
      // Generic result with no rich surface. The action header already names
      // the step (with a status icon), so a body that just repeats the tool
      // name is noise — only render when the result carries real content.
      const message =
        typeof result.message === 'string' ? result.message : undefined;
      const info = typeof result.info === 'string' ? result.info : undefined;
      if (!message && !info) return null;
      return (
        <ResultNotice
          icon={Check}
          title={message ?? info ?? ''}
          description={message ? info : undefined}
        />
      );
    }
  }
}

const ToolResultSurface = ({
  args,
  result,
  toolCallId,
}: ToolCallMessagePartProps<ToolResultArgs, ToolResultPayload>) => {
  const resolvedResult = (result ?? {}) as ToolResultPayload;
  const displayType =
    args.displayType ?? resolvedResult.__displayType ?? 'generic';
  const memoryEnabled = useCluMemoryEnabled();
  // An agent tool's result refreshes what it changed, once, wherever it shows.
  useAgentDisplayEffects({
    id: toolCallId,
    displayType,
    result: resolvedResult,
  });

  // What Clu learns shows only while the organization may use it: the memory
  // tools' results draw nothing otherwise, whatever
  // their display (the agents look hides them with the steps).
  if (!memoryEnabled && isMemoryTool(args.name)) return null;
  // What Clu learned: one row under its reply.
  if (displayType === LEARNING_DISPLAY) {
    return <LearningPart display={resolvedResult.__display} />;
  }

  // Clu's cards carry their own header; no action line above them.
  if (displayType === QUESTION_CARD_DISPLAY) {
    return (
      <div data-timeline-action>
        <QuestionCardPart
          toolCallId={toolCallId}
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === 'quick_replies') {
    return (
      <div data-timeline-action>
        <QuickRepliesPart
          display={resolvedResult.__display}
          result={resolvedResult}
          toolCallId={toolCallId}
        />
      </div>
    );
  }
  // A step the plan leaves out: the upgrade card, never an error line (its
  // result carries the refusal under `error`).
  if (displayType === 'upgrade_prompt') {
    return (
      <div data-timeline-action>
        <PlanRequiredCard result={resolvedResult} />
      </div>
    );
  }
  if (displayType === 'first_run_options') {
    return (
      <div data-timeline-action>
        <FirstRunOptionsCard
          result={resolvedResult}
          display={resolvedResult.__display}
        />
      </div>
    );
  }
  if (displayType === 'play_proposal') {
    return (
      <div data-timeline-action>
        <PlayProposalCard result={resolvedResult} />
      </div>
    );
  }
  if (
    displayType === ONE_OFF_RUN_STARTED ||
    displayType === AGENT_RUN_STARTED
  ) {
    return (
      <div data-timeline-action>
        <OneOffRunCard
          displayType={displayType}
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === DRAFT_AGENT_CREATED) {
    return (
      <div data-timeline-action>
        <DraftAgentCard
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === REPEAT_AGENT_CREATED) {
    return (
      <div data-timeline-action>
        <RepeatAgentCard
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === 'google_sheet_import') {
    if (resolvedResult.mode === 'read')
      return (
        <div data-timeline-action>
          <GoogleSheetReadCard
            result={resolvedResult}
            toolCallId={toolCallId}
          />
        </div>
      );
    if (resolvedResult.mode === 'for_trigger')
      return (
        <div data-timeline-action>
          <SheetTriggerPickCard
            result={resolvedResult}
            toolCallId={toolCallId}
          />
        </div>
      );
    return (
      <div data-timeline-action>
        <GoogleSheetImportCard result={resolvedResult} />
      </div>
    );
  }
  if (displayType === 'pasted_rows_import') {
    return (
      <div data-timeline-action>
        <PastedRowsImportCard result={resolvedResult} />
      </div>
    );
  }
  if (displayType === 'google_doc_read') {
    return (
      <div data-timeline-action>
        <GoogleDocReadCard result={resolvedResult} toolCallId={toolCallId} />
      </div>
    );
  }
  if (displayType === 'google_doc_pick') {
    return (
      <div data-timeline-action>
        <GoogleDocPickCard result={resolvedResult} toolCallId={toolCallId} />
      </div>
    );
  }
  if (displayType === 'connection_required') {
    return (
      <div data-timeline-action>
        <ConnectionRequiredCard
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === 'run_diagnosis') {
    return (
      <div data-timeline-action>
        <RunDiagnosisActions
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }
  if (displayType === MERGE_STARTED_DISPLAY) {
    return (
      <div data-timeline-action>
        <DuplicateMergeStartedCard
          display={resolvedResult.__display}
          result={resolvedResult}
        />
      </div>
    );
  }

  return (
    <ToolResultActionSurface
      args={args}
      resolvedResult={resolvedResult}
      displayType={displayType}
    />
  );
};

const ToolResultActionSurface = ({
  args,
  resolvedResult,
  displayType,
}: {
  args: ToolResultArgs;
  resolvedResult: ToolResultPayload;
  displayType: string;
}) => {
  const listId =
    typeof resolvedResult.list_id === 'string'
      ? resolvedResult.list_id
      : undefined;
  const isError = Boolean(resolvedResult.error);

  // When THIS exact people result is mirrored in the home Workspace pane, don't
  // render the full surface inline too — collapse to a compact reference. Only
  // the result currently in the pane is suppressed (older ones stay inline);
  // outside the home split mirroredResultId is null, so nothing is suppressed.
  const { mirroredResultId } = useCopilotPane();
  const resultId =
    typeof resolvedResult.__toolResultId === 'string'
      ? resolvedResult.__toolResultId
      : null;
  const isPeopleSurface =
    displayType === 'people_table' && args.name !== 'people_search';
  const mirroredInPane =
    !isError &&
    isPeopleSurface &&
    resultId !== null &&
    resultId === mirroredResultId;

  if (args.name === 'run_agent_sample' && isError)
    return <TryRequestFailure result={resolvedResult} />;

  // The result in the agent card's shell, in every chat, no timeline line.
  // A failure with words is a step in the agents chat, which says why, so it
  // never reaches here there; the older thread has no steps line, so it reads
  // the failure on one line.
  if (isError) {
    return typeof resolvedResult.error === 'string' ? (
      <ResultNotice
        icon={AlertTriangle}
        variant='error'
        title={resolvedResult.error}
      />
    ) : null;
  }
  return (
    <GumloopResultCard
      displayType={displayType}
      toolName={args.name}
      result={resolvedResult}
      mirrored={mirroredInPane}
      legacy={() => renderToolResult(displayType, resolvedResult, listId, args)}
    />
  );
};

const ToolResultToolUi = makeAssistantToolUI<ToolResultArgs, ToolResultPayload>(
  {
    toolName: 'tool_result',
    render: ToolResultSurface,
  }
);

function buildImportTitle(count: number | null, listName?: string): string {
  const leadLabel =
    count === null
      ? 'leads'
      : `${count.toLocaleString()} ${count === 1 ? 'lead' : 'leads'}`;
  if (listName) return `Import ${leadLabel} into "${listName}"`;
  return `Import ${leadLabel}`;
}

/** What a Confirm click carries: the action and token of the card shown, so
 *  the hook confirms only the pending card that was clicked. */
const cardIdentity = (request: ConfirmationRequest): ConfirmationIdentity => ({
  action: request.tool_name,
  actionToken: request.action_token,
});

/** Clean a description: strips credit info, markdown bold, quotes, and normalises action wording. */
const cleanDescription = (description?: string): string => {
  if (!description) return '';
  return (
    description
      // Remove parenthesized credit info
      .replace(/\s*\(~?[\d.,]+\s*credits?[^)]*\)/gi, '')
      .replace(/\s*\(you have [\d.,]+ available\)/gi, '')
      // Remove markdown bold markers
      .replace(/\*\*/g, '')
      // Remove quotes around names
      .replace(/"/g, '')
      // An open card says what it will do: the answered card has its own
      // past-tense title, so nothing here turns "Run" into "Ran".
      .trim()
  );
};

/** The balance the server sent with a card, or null when it sent none. */
const balanceOf = (request: ConfirmationRequest): number | null =>
  typeof request.credits_available === 'number' &&
  Number.isFinite(request.credits_available)
    ? request.credits_available
    : null;

/** Clu adding what a play enriches to the org's default CRM field mapping. */
const ADD_FIELD_MAPPINGS_ACTION = 'add_crm_field_mappings';

/** Clu's sequence changes and one-off sends. None is a delete, though an
 *  edit can remove a step and a sequence's name or a Slack message's text can
 *  hold the word, so the delete look (which reads the description) never
 *  applies to them. */
const SEQUENCE_ACTIONS: ReadonlySet<string> = new Set([
  'create_sequence',
  'edit_sequence',
  'set_sequence_status',
  'enroll_in_sequence',
  SEND_MAILBOX_EMAIL_ACTION,
  REPLY_IN_THREAD_ACTION,
  SEND_SLACK_MESSAGE_ACTION,
  SEND_LINKEDIN_ACTION,
  // A campaign's name or an email's text can hold the word too.
  ...INSTANTLY_ACTIONS,
]);

/** Whether a confirmation reads as a delete. Removing Instantly leads always
 *  does (it deletes them for good), whatever its words say. Any other free
 *  action does when its name or description says "delete", unless it is in
 *  SEQUENCE_ACTIONS. */
function isDeleteCard(request: ConfirmationRequest, cost: number): boolean {
  if (isInstantlyRemove(request)) return true;
  // A connection write never deletes, and its undo can read like one.
  if (request.tool_name === CONNECTION_API_WRITE_ACTION) return false;
  return (
    cost <= 0 &&
    !SEQUENCE_ACTIONS.has(request.tool_name ?? '') &&
    Boolean(
      request.tool_name?.includes('delete') ||
      request.description?.toLowerCase().includes('delete')
    )
  );
}

/**
 * What a confirmation card says (the card standard, CLE-606): its title,
 * verb first; its main button and the words the button shows once clicked;
 * its title once done; its icon; and whether the button is the destructive
 * one (a delete, a merge).
 */
interface ConfirmationCopy {
  title: string;
  action: string;
  working: string;
  done: string;
  icon: IconDefinition;
  danger: boolean;
}

const copyOf = (
  title: string,
  action: string,
  working: string,
  done: string,
  icon: IconDefinition,
  danger = false
): ConfirmationCopy => ({ title, action, working, done, icon, danger });

/** Each action Clu asks to confirm whose words don't hang on its arguments. */
const CONFIRMATION_COPY: Readonly<Record<string, ConfirmationCopy>> = {
  [SEND_MAILBOX_EMAIL_ACTION]: copyOf(
    'Send email',
    'Send',
    'Sending',
    'Sent email',
    iconEnvelope
  ),
  [REPLY_IN_THREAD_ACTION]: copyOf(
    'Reply in thread',
    'Send',
    'Sending',
    'Replied in thread',
    iconReply
  ),
  [CONNECTION_API_WRITE_ACTION]: copyOf(
    'Change a record',
    'Confirm',
    'Confirming',
    'Changed a record',
    iconPlug
  ),
  [SEND_SLACK_MESSAGE_ACTION]: copyOf(
    'Send Slack message',
    'Send',
    'Sending',
    'Sent Slack message',
    iconSlack
  ),
  delete_lead_list: copyOf(
    'Delete list',
    'Delete',
    'Deleting',
    'Deleted list',
    iconTrash,
    true
  ),
  delete_leads: copyOf(
    'Delete leads',
    'Delete',
    'Deleting',
    'Deleted leads',
    iconTrash,
    true
  ),
  [MERGE_DUPLICATES_ACTION]: copyOf(
    'Merge duplicates',
    'Merge',
    'Merging',
    'Merged duplicates',
    iconLayerGroup,
    true
  ),
  dismiss_duplicate_groups: copyOf(
    'Dismiss duplicates',
    'Dismiss',
    'Dismissing',
    'Dismissed duplicates',
    iconLayerGroup
  ),
  skip_duplicate_groups: copyOf(
    'Skip duplicates',
    'Skip',
    'Skipping',
    'Skipped duplicates',
    iconLayerGroup
  ),
  create_sequence: copyOf(
    'Create sequence',
    'Create',
    'Creating',
    'Created sequence',
    iconPaperPlane
  ),
  edit_sequence: copyOf(
    'Edit sequence',
    'Save',
    'Saving',
    'Edited sequence',
    iconPaperPlane
  ),
  enroll_in_sequence: copyOf(
    'Add to sequence',
    'Add',
    'Adding',
    'Added to sequence',
    iconUserPlus
  ),
  import_to_list: copyOf(
    'Import leads',
    'Import',
    'Importing',
    'Imported leads',
    iconUsers
  ),
  enrich_leads: copyOf(
    'Enrich leads',
    'Enrich',
    'Enriching',
    'Enriched leads',
    iconWandMagicSparkles
  ),
  create_smart_column: copyOf(
    'Add smart column',
    'Add',
    'Adding',
    'Added smart column',
    iconTableColumns
  ),
  move_leads: copyOf(
    'Move leads',
    'Move',
    'Moving',
    'Moved leads',
    iconArrowRight
  ),
  reverse_email_lookup: copyOf(
    'Look up email',
    'Look up',
    'Looking up',
    'Looked up email',
    iconUserMagnifyingGlass
  ),
  prospect_person: copyOf(
    'Find person',
    'Find',
    'Finding',
    'Found person',
    iconUserMagnifyingGlass
  ),
  validate_contacts: copyOf(
    'Check contacts',
    'Check',
    'Checking',
    'Checked contacts',
    iconEnvelopeCircleCheck
  ),
  [ADD_FIELD_MAPPINGS_ACTION]: copyOf(
    'Save field mapping',
    'Save',
    'Saving',
    'Saved field mapping',
    iconArrowsLeftRight
  ),
  update_company_profile: copyOf(
    'Update profile',
    'Save',
    'Saving',
    'Updated profile',
    iconBuilding
  ),
  create_play_webhook_endpoint: copyOf(
    'Create webhook',
    'Create',
    'Creating',
    'Created webhook',
    iconWebhook
  ),
  set_agent_limits: copyOf(
    'Set agent limits',
    'Save',
    'Saving',
    'Set agent limits',
    iconSliders
  ),
  import_slack_file: copyOf(
    'Import file',
    'Import',
    'Importing',
    'Imported file',
    iconFileArrowUp
  ),
  run_agent_test: copyOf('Run test', 'Run', 'Starting', 'Ran test', iconFlask),
  run_agent_sample: copyOf(
    'Try it',
    'Try it',
    'Starting',
    'Tried it',
    iconFlask
  ),
  run_slack_agent_now: copyOf(
    'Run agent',
    'Run',
    'Starting',
    'Ran agent',
    iconPlay
  ),
  instantly_create_campaign: copyOf(
    'Create campaign in Instantly',
    'Create',
    'Creating',
    'Created the campaign in Instantly',
    iconEnvelope
  ),
  instantly_edit_campaign: copyOf(
    'Edit campaign in Instantly',
    'Save',
    'Saving',
    'Edited the campaign in Instantly',
    iconPen
  ),
  instantly_add_leads: copyOf(
    'Add leads in Instantly',
    'Add',
    'Adding',
    'Added leads in Instantly',
    iconUserPlus
  ),
  instantly_blocklist_add: copyOf(
    'Add to Instantly blocklist',
    'Add',
    'Adding',
    'Added to the Instantly blocklist',
    iconBan
  ),
  instantly_resume_webhook: copyOf(
    'Resume Instantly webhook',
    'Resume',
    'Resuming',
    'Resumed the Instantly webhook',
    iconWebhook
  ),
};

const CONFIRM_COPY = copyOf(
  'Confirm',
  'Confirm',
  'Confirming',
  'Confirmed',
  iconCircleCheck
);

/** A connection write card's title verbs, done. */
const PAST_TENSE: Readonly<Record<string, string>> = {
  Change: 'Changed',
  Edit: 'Edited',
  Update: 'Updated',
  Pause: 'Paused',
  Create: 'Created',
  Add: 'Added',
  Remove: 'Removed',
  Undo: 'Undid',
};

/** The words for a card whose action hangs on its arguments, else the table's. */
function actionCopy(request: ConfirmationRequest): ConfirmationCopy {
  const args = request.arguments ?? {};
  switch (request.tool_name) {
    case 'set_agent_status':
      if (args.status === 'active')
        return copyOf(
          'Turn on agent',
          'Turn on',
          'Turning on',
          'Turned on agent',
          iconToggleOn
        );
      if (args.status === 'paused' || args.status === 'inactive')
        return copyOf(
          'Turn off agent',
          'Turn off',
          'Turning off',
          'Turned off agent',
          iconToggleOn
        );
      return { ...CONFIRM_COPY, icon: iconToggleOn };
    case 'set_sequence_status':
      if (args.action === 'pause')
        return copyOf(
          'Pause sequence',
          'Pause',
          'Pausing',
          'Paused sequence',
          iconPause
        );
      if (args.action === 'resume')
        return copyOf(
          'Resume sequence',
          'Resume',
          'Resuming',
          'Resumed sequence',
          iconPlay
        );
      if (args.action === 'activate')
        return copyOf(
          'Launch sequence',
          'Launch',
          'Launching',
          'Launched sequence',
          iconRocket
        );
      return { ...CONFIRM_COPY, icon: iconPaperPlane };
    case SEND_LINKEDIN_ACTION:
      return args.action === 'invite'
        ? copyOf(
            'Send LinkedIn invite',
            'Send',
            'Sending',
            'Sent LinkedIn invite',
            iconLinkedin
          )
        : copyOf(
            'Send LinkedIn message',
            'Send',
            'Sending',
            'Sent LinkedIn message',
            iconLinkedin
          );
    case 'change_clu_memory':
      return args.forget === true
        ? copyOf(
            'Forget note',
            'Forget',
            'Forgetting',
            'Forgot note',
            iconBrain
          )
        : copyOf('Save note', 'Save', 'Saving', 'Saved note', iconBrain);
    case 'create_crm_activity': {
      const noun =
        args.kind === 'note'
          ? 'note'
          : args.kind === 'task'
            ? 'task'
            : 'activity';
      return copyOf(
        `Create CRM ${noun}`,
        'Create',
        'Creating',
        `Created CRM ${noun}`,
        iconListCheck
      );
    }
    case 'instantly_campaign_status':
      if (args.action === 'activate')
        return copyOf(
          'Activate campaign in Instantly',
          'Activate',
          'Activating',
          'Activated the campaign in Instantly',
          iconToggleOn
        );
      if (args.action === 'pause')
        return copyOf(
          'Pause campaign in Instantly',
          'Pause',
          'Pausing',
          'Paused the campaign in Instantly',
          iconPause
        );
      return copyOf(
        'Change campaign in Instantly',
        'Confirm',
        'Confirming',
        'Changed the campaign in Instantly',
        iconToggleOn
      );
    case 'instantly_change_leads':
      if (args.action === 'move')
        return copyOf(
          'Move leads in Instantly',
          'Move',
          'Moving',
          'Moved leads in Instantly',
          iconArrowRight
        );
      if (args.action === 'remove')
        return copyOf(
          'Remove leads in Instantly',
          'Remove',
          'Removing',
          'Removed leads in Instantly',
          iconTrash,
          true
        );
      if (args.action === 'set_interest')
        return copyOf(
          'Set lead interest in Instantly',
          'Save',
          'Saving',
          'Set lead interest in Instantly',
          iconUsers
        );
      return copyOf(
        'Change leads in Instantly',
        'Confirm',
        'Confirming',
        'Changed leads in Instantly',
        iconUsers
      );
    case 'instantly_warmup':
      if (args.on === true)
        return copyOf(
          'Turn on warmup in Instantly',
          'Turn on',
          'Turning on',
          'Turned on warmup in Instantly',
          iconWavePulse
        );
      if (args.on === false)
        return copyOf(
          'Turn off warmup in Instantly',
          'Turn off',
          'Turning off',
          'Turned off warmup in Instantly',
          iconWavePulse
        );
      return copyOf(
        'Change warmup in Instantly',
        'Confirm',
        'Confirming',
        'Changed warmup in Instantly',
        iconWavePulse
      );
    case CONNECTION_API_WRITE_ACTION: {
      // The server titles the card by what it changes (Edit a doc).
      const copy = CONFIRMATION_COPY[CONNECTION_API_WRITE_ACTION];
      const title =
        typeof request.title === 'string' ? request.title.trim() : '';
      const [verb, ...rest] = title.split(' ');
      const past = PAST_TENSE[verb];
      return title
        ? {
            ...copy,
            title,
            done: past ? [past, ...rest].join(' ') : copy.done,
          }
        : copy;
    }
    default:
      return CONFIRMATION_COPY[request.tool_name] ?? CONFIRM_COPY;
  }
}

/**
 * The card's words, with the delete look the old card read from the
 * description too: a free action that names a delete, unless it is a
 * sequence change or a send, whose text can hold the word.
 */
function confirmationCopy(request: ConfirmationRequest): ConfirmationCopy {
  const copy = actionCopy(request);
  const namesDelete = isDeleteCard(request, toNumber(request.credits_required));
  if (copy.danger || !namesDelete) return copy;
  return {
    ...copy,
    danger: true,
    icon: copy === CONFIRM_COPY ? iconTrash : copy.icon,
  };
}

/**
 * CardFooter locks its buttons while a confirm runs and keeps that to
 * itself; a card with inputs in its body (an email's edits, the import
 * count) locks them for the same time with this. A failed confirm that
 * wasn't aborted, or one that threw, unlocks them, as it unlocks the
 * buttons.
 */
function useConfirming() {
  const [confirming, setConfirming] = useState(false);
  const track = useCallback(
    async (confirm: () => void | Promise<ConfirmResult>) => {
      setConfirming(true);
      try {
        const result = await confirm();
        if (result && !result.ok && !result.aborted) setConfirming(false);
        return result;
      } catch (error) {
        setConfirming(false);
        throw error;
      }
    },
    []
  );
  return [confirming, track] as const;
}

/** A header's end: a status, a cost or the time left, off the edge. */
const HeaderEnd = ({ children }: { children: ReactNode }) => (
  <span className='flex items-center gap-[8px] pr-[4px]'>{children}</span>
);

/** The confirmation card's header end: the credits, then the time left. */
const confirmationTrailing = (
  cost: number,
  secondsLeft: number | null,
  short = false
): ReactNode => {
  const counting =
    secondsLeft !== null &&
    secondsLeft > 0 &&
    secondsLeft <= COUNTDOWN_SHOWN_FROM_SECONDS;
  return cost <= 0 && !counting ? undefined : (
    <HeaderEnd>
      {cost > 0 ? <CardCost amount={cost} short={short} /> : null}
      {counting ? <CountdownBadge secondsLeft={secondsLeft} /> : null}
    </HeaderEnd>
  );
};

/** An expired card's header end. */
const EXPIRED = (
  <HeaderEnd>
    <CardStatus>Expired</CardStatus>
  </HeaderEnd>
);

/** True when the user cancelled this message's card: the dock's echo of
 *  Cancel follows it. A reloaded thread keeps no echo, so it can't say. */
const cancelledAfter = (
  messages: readonly { id: string; role: string }[],
  messageId: string
): boolean => {
  const index = messages.findIndex((message) => message.id === messageId);
  const next = index >= 0 ? messages[index + 1] : undefined;
  return next?.role === 'user' && next.id.startsWith('user-cancel-');
};

/** A connection write card's title once confirmed, from the write's result
 *  after it: its done words when it landed, the step's words when it didn't
 *  or may have, and its ask while no result has come. */
const connectionWriteTitle = (
  messages: readonly { id: string; role: string; content: unknown }[],
  messageId: string,
  copy: ConfirmationCopy
): string => {
  const index = messages.findIndex((message) => message.id === messageId);
  for (const message of index >= 0 ? messages.slice(index + 1) : []) {
    if (message.role !== 'assistant' || !Array.isArray(message.content))
      continue;
    for (const part of message.content as readonly ThreadPartLike[]) {
      if (part.type !== 'tool-call') continue;
      // A later card: this one's result never came.
      if (part.toolName === 'confirmation_request') return copy.title;
      if (asRecord(part.args)?.name !== CONNECTION_API_WRITE_ACTION) continue;
      const result = asRecord(part.result) ?? {};
      const label = resultStepLabel(CONNECTION_API_WRITE_ACTION, result);
      return label === resultStepLabel(CONNECTION_API_WRITE_ACTION, {})
        ? copy.done
        : label;
    }
  }
  return copy.title;
};

/**
 * A confirmation that is no longer pending, on one line: what it did with
 * its cost, or what it would have done with Cancelled or Expired.
 */
const ConfirmationResolvedNotice = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const copy = confirmationCopy(request);
  const cancelled = useAuiState((s) =>
    cancelledAfter(s.thread.messages, s.message.id)
  );
  const done = useAuiState((s) =>
    request.tool_name === CONNECTION_API_WRITE_ACTION
      ? connectionWriteTitle(s.thread.messages, s.message.id, copy)
      : copy.done
  );
  const cost = toNumber(request.credits_required);

  const expiresAt = request.expires_at
    ? parseServerDate(request.expires_at)
    : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;
  const status = cancelled ? 'Cancelled' : isExpired ? 'Expired' : null;

  return (
    <AgentCard
      icon={copy.icon}
      title={status ? copy.title : done}
      data-confirmation-resolved
      trailing={
        status ? (
          <HeaderEnd>
            <CardStatus>{status}</CardStatus>
          </HeaderEnd>
        ) : cost > 0 ? (
          <HeaderEnd>
            <CardCost amount={cost} />
          </HeaderEnd>
        ) : undefined
      }
    />
  );
};

/**
 * A confirmation in every chat: the agent card with what it does as its
 * title ("Turn on agent", "Send email"), the cost in the header, the
 * server's description, a send's preview, and Cancel and the action's own
 * button, which locks both until the action answers and unlocks them when
 * it fails. When the balance the server sent can't cover the cost, the
 * button is Buy credits.
 */
export const AgentConfirmationCard = ({
  request,
  icon: iconOverride,
}: {
  request: ConfirmationRequest;
  /** The card's own icon, such as a merge card's; else one for its action. */
  icon?: IconDefinition;
}) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const [confirming, track] = useConfirming();
  const email = useEmailEdits(request);
  const copy = confirmationCopy(request);
  const icon = iconOverride ?? copy.icon;
  const cost = toNumber(request.credits_required);
  const balance = useCardBalance(balanceOf(request));
  const short = cost > 0 && balance !== null && cost > balance;

  if (isExpired) {
    return (
      <AgentCard
        icon={icon}
        title={copy.title}
        data-agent-confirmation
        trailing={EXPIRED}
      />
    );
  }

  // A connection write's text is the server's own, quotes and all.
  const description =
    request.tool_name === CONNECTION_API_WRITE_ACTION
      ? confirmationDescription(request).trim()
      : cleanDescription(confirmationDescription(request));
  return (
    <AgentCard
      icon={icon}
      title={copy.title}
      data-agent-confirmation
      trailing={confirmationTrailing(cost, secondsLeft, short)}
    >
      {description ? <CardText lines={0}>{description}</CardText> : null}
      {short && balance !== null ? <CardShortOfCredits have={balance} /> : null}
      <SendEmailPreview
        request={request}
        edits={email.edits}
        onEdit={email.setEdits}
        disabled={confirming}
      />
      <ReplyInThreadPreview request={request} />
      <SendSlackMessagePreview request={request} />
      <SendLinkedinPreview request={request} />
      <ConnectionApiWritePreview request={request} />
      <CardFooter
        cancel={{ onClick: onReject }}
        primary={{
          label: copy.action,
          workingLabel: copy.working,
          tone: copy.danger ? 'danger' : undefined,
          disabled: email.invalid,
          onClick: () =>
            track(() =>
              onConfirm(email.edits ?? undefined, cardIdentity(request))
            ),
        }}
        short={short ? { needed: cost } : undefined}
      />
    </AgentCard>
  );
};

/**
 * The import confirmation: what it imports, the enrichment to add with its
 * cost a lead, how many leads, then Cancel and Import. The header holds the
 * total. When the balance the server sent can't cover it, Import becomes
 * Buy credits until the count or the enrichment fits.
 */
const AgentImportConfirmationCard = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const maxCount = importMaxCount(request);
  const [selectedCount, setSelectedCount] = useState(maxCount);
  const [selectedEnrichment, setSelectedEnrichment] =
    useState<(typeof ENRICHMENT_OPTIONS)[number]['id']>('none');
  const [confirming, track] = useConfirming();

  useEffect(() => {
    setSelectedCount(maxCount);
    setSelectedEnrichment('none');
  }, [maxCount, request.id]);

  const enrichmentOption = ENRICHMENT_OPTIONS.find(
    (option) => option.id === selectedEnrichment
  );
  // Import is free; only enrichment costs credits. With no count there is
  // no total to quote, only the per-lead price on each row.
  const totalCost = (enrichmentOption?.cost ?? 0) * (selectedCount ?? 0);
  const balance = useCardBalance(balanceOf(request));
  const short = totalCost > 0 && balance !== null && totalCost > balance;
  const listName =
    typeof request.arguments?.list_name === 'string'
      ? request.arguments.list_name
      : undefined;
  // The search matched more than one import adds: the same line as Find
  // People's Add to list.
  const cap =
    maxCount === null
      ? null
      : importCap({
          matches:
            typeof request.arguments?.matches === 'number'
              ? request.arguments.matches
              : undefined,
          planLimit: null,
          requestCeiling: maxCount,
        });

  if (isExpired) {
    return (
      <AgentCard
        icon={iconUsers}
        title='Import leads'
        data-agent-confirmation
        trailing={EXPIRED}
      />
    );
  }

  return (
    <AgentCard
      icon={iconUsers}
      title='Import leads'
      data-agent-confirmation
      trailing={confirmationTrailing(totalCost, secondsLeft, short)}
    >
      <CardText lines={0}>{buildImportTitle(selectedCount, listName)}</CardText>
      {short && balance !== null ? (
        <CardShortOfCredits
          have={balance}
          hint='Lower the count or add credits.'
        />
      ) : null}
      <CardRows label='Enrichment' selectable>
        {ENRICHMENT_OPTIONS.map((option) => (
          <CardRow
            key={option.id}
            selected={selectedEnrichment === option.id}
            onSelect={() => setSelectedEnrichment(option.id)}
            disabled={confirming}
            label={option.label}
            end={
              option.cost > 0 ? (
                <CardCost amount={option.cost} unit='lead' />
              ) : (
                <CardFree />
              )
            }
          />
        ))}
      </CardRows>
      {cap ? <ImportCapNotice {...cap} showCeiling /> : null}
      <CardFooter
        control={
          maxCount !== null && selectedCount !== null && maxCount > 1 ? (
            <ImportCountField
              value={selectedCount}
              max={maxCount}
              onChange={setSelectedCount}
              disabled={confirming}
            />
          ) : undefined
        }
        cancel={{ onClick: onReject }}
        primary={{
          label: 'Import',
          workingLabel: 'Importing',
          onClick: () =>
            track(() =>
              onConfirm(
                {
                  enrichment_type: selectedEnrichment,
                  ...(selectedCount !== null ? { count: selectedCount } : {}),
                },
                cardIdentity(request)
              )
            ),
        }}
        short={short ? { needed: totalCost } : undefined}
      />
    </AgentCard>
  );
};

/**
 * Merging duplicate groups from Clu (W5 design 8.5 and 10.5), in the agent
 * card's shell: the review's own confirm ("Merge 8 groups in Salesforce?"
 * and that it can't be undone), the groups by object, the first five groups
 * with the record each keeps and how many more, "Review duplicates", and
 * Cancel and Merge. Merge is off, with the review's reason beside it, for
 * anyone but an org admin or the CRM's connector and while the agent is off;
 * the server checks both again when it runs. Whether the agent is on comes
 * from the agent itself, as the review reads it, so switching it on or off
 * while the card waits turns Merge on or off; the card's own word stands only
 * until the agent loads. A card whose details can't be read shows the
 * server's description in the plain confirmation. Part of Agents: shown,
 * and the agent read, only with Agents unlocked; otherwise, or while it
 * loads, ConfirmationRequestSurface asks in the plain confirmation instead.
 */
export const MergeDuplicatesConfirmationCard = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const { unlocked } = useAgentsAccess();
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const view = useMemo(() => mergeCardOf(request), [request]);
  // The card's agent, else the dock's, as "Review duplicates" opens.
  const dockAgentId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'agent' ? context.agent_id : undefined;
  });
  const agentId = view ? (view.agentId ?? dockAgentId) : undefined;
  const { currentData: agent } = useGetAgentQuery(
    unlocked && agentId ? agentId : skipToken
  );

  if (!unlocked) return null;
  if (!view)
    return <AgentConfirmationCard request={request} icon={iconLayerGroup} />;

  if (isExpired) {
    return (
      <AgentCard
        icon={iconLayerGroup}
        title='Merge duplicates'
        data-merge-duplicates-confirmation
        trailing={EXPIRED}
      />
    );
  }

  const confirm = mergeConfirm(view.groupCount, view.crmType);
  const blocked = mergeBlockedReason({
    ...view,
    agentOn: agent ? agent.status === 'active' : view.agentOn,
  });
  const objects = mergeObjectsLine(view.byObject);

  return (
    <AgentCard
      icon={iconLayerGroup}
      title='Merge duplicates'
      data-merge-duplicates-confirmation
      trailing={confirmationTrailing(0, secondsLeft)}
    >
      <CardText lines={0}>{`${confirm.title} This can't be undone.`}</CardText>
      {objects ? <CardMeta>{objects}</CardMeta> : null}
      {view.groups.length ? (
        <div className='flex min-w-0 flex-col gap-[8px]'>
          <ul
            aria-label='Groups to merge'
            className='flex min-w-0 flex-col gap-[4px]'
          >
            {view.groups.map((group) => (
              <li key={group.id} className='min-w-0'>
                <CardRow
                  label={
                    group.flags.length ? (
                      <span className='flex min-w-0 flex-col'>
                        <span className='truncate'>{groupName(group)}</span>
                        <span className='truncate font-normal text-amber-600 dark:text-amber-400'>
                          {flagsLine(group)}
                        </span>
                      </span>
                    ) : (
                      groupName(group)
                    )
                  }
                  end={keptLine(group)}
                />
              </li>
            ))}
          </ul>
          {view.more > 0 ? (
            <CardMeta>{view.more.toLocaleString() + ' more'}</CardMeta>
          ) : null}
        </div>
      ) : null}
      <CardFooter
        links={
          <span className='text-[12px] leading-[16px]'>
            <ReviewDuplicatesLink agentId={view.agentId} />
          </span>
        }
        control={blocked ? <CardMeta>{blocked}</CardMeta> : undefined}
        cancel={{ onClick: onReject }}
        primary={{
          label: 'Merge',
          workingLabel: 'Merging',
          tone: 'danger',
          disabled: Boolean(blocked),
          // As the other confirmations: a failed confirm didn't claim the
          // action, so Merge unlocks for a retry.
          onClick: () => onConfirm(undefined, cardIdentity(request)),
        }}
      />
    </AgentCard>
  );
};

/**
 * Which list: one row a list with its leads, then Cancel and Use list. Use
 * list sends once: a second confirm with the same turn id would abort the
 * first one's stream. A failed confirm unlocks it for a retry.
 */
const AgentListPickCard = ({ request }: { request: ConfirmationRequest }) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const lists = request.lists ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, track] = useConfirming();
  const chosen = lists.find((list) => list.id === selected);

  return (
    <AgentCard icon={iconTable} title='Choose a list' data-agent-list-pick>
      <CardRows label='Lists' selectable>
        {lists.map((list) => (
          <CardRow
            key={list.id}
            selected={selected === list.id}
            onSelect={() => setSelected(list.id)}
            disabled={confirming}
            label={list.name}
            end={`${list.lead_count.toLocaleString()} ${
              list.lead_count === 1 ? 'lead' : 'leads'
            }`}
          />
        ))}
      </CardRows>
      <CardFooter
        cancel={{ onClick: onReject }}
        primary={{
          label: 'Use list',
          disabled: !chosen,
          onClick: () =>
            chosen
              ? track(() =>
                  onConfirm(
                    {
                      list_id: chosen.id,
                      list_name: chosen.name,
                      _disambiguation: true,
                    },
                    cardIdentity(request)
                  )
                )
              : false,
        }}
      />
    </AgentCard>
  );
};

/** A one-off play, a live run of an agent, a repeat or a draft agent: its own
 * card. */
const OneOffConfirmationSurface = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  return (
    <OneOffConfirmationCard
      request={request}
      onConfirm={() => onConfirm(undefined, cardIdentity(request))}
      onReject={onReject}
      secondsLeft={secondsLeft}
      countdown={
        secondsLeft !== null ? (
          <CountdownBadge secondsLeft={secondsLeft} />
        ) : null
      }
    />
  );
};

/** A list column added, its values set, a skill run on a list: its own
 *  card, shown only with Agents unlocked. */
const ListShapingConfirmationSurface = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  return (
    <ListShapingConfirmationCard
      request={request}
      onConfirm={() => onConfirm(undefined, cardIdentity(request))}
      onReject={onReject}
      secondsLeft={secondsLeft}
      countdown={
        secondsLeft !== null ? (
          <CountdownBadge secondsLeft={secondsLeft} />
        ) : null
      }
    />
  );
};

const TryConfirmationSurface = ({
  request,
}: {
  request: ConfirmationRequest;
}) => {
  const { onConfirm, onReject } = useCopilotToolUiContext();
  const secondsLeft = useCountdown(request.expires_at);
  return (
    <TryChatCard
      key={request.action_token}
      request={request}
      expired={secondsLeft !== null && secondsLeft <= 0}
      onConfirm={onConfirm}
      onReject={onReject}
    />
  );
};

const ONE_OFF_SEND_ACTIONS = new Set<string>([
  SEND_MAILBOX_EMAIL_ACTION,
  REPLY_IN_THREAD_ACTION,
  SEND_SLACK_MESSAGE_ACTION,
  SEND_LINKEDIN_ACTION,
]);

const ConfirmationRequestSurface = ({
  args,
}: ToolCallMessagePartProps<ConfirmationRequest, unknown>) => {
  const { pendingConfirmationId, pendingConfirmationActionToken } =
    useCopilotToolUiContext();
  const { unlocked } = useAgentsAccess();
  // With Try it (try_it_v1) on, a dry run request can no longer run.
  const tryIt = useTryIt() === true;
  const instantly = useInstantlyEnabled();

  // Instantly's cards show only with instantly_v1 on.
  if (INSTANTLY_ACTIONS.has(args.tool_name) && !instantly) return null;

  let content: ReactNode;

  // Show non-interactive resolved state for past confirmations. Every send,
  // import or connection write card of one kind shares one id, so only the
  // pending token's card stays live.
  if (
    args.id !== pendingConfirmationId ||
    (args.tool_name === 'run_agent_sample' &&
      (!args.action_token ||
        args.action_token !== pendingConfirmationActionToken)) ||
    ((ONE_OFF_SEND_ACTIONS.has(args.tool_name) ||
      args.tool_name === 'import_to_list' ||
      args.tool_name === CONNECTION_API_WRITE_ACTION) &&
      !!pendingConfirmationActionToken &&
      args.action_token !== pendingConfirmationActionToken)
  ) {
    // A newer import card replaced this one: whether it ran shows in its
    // result, so the card itself only closes.
    const replacedImport =
      args.tool_name === 'import_to_list' &&
      args.id === pendingConfirmationId &&
      !!pendingConfirmationActionToken &&
      args.action_token !== pendingConfirmationActionToken;
    content =
      replacedImport ||
      args.tool_name === 'run_agent_sample' ||
      (tryIt && args.tool_name === 'run_agent_test') ? (
        <AgentCard
          icon={replacedImport ? confirmationCopy(args).icon : iconFlask}
          title={confirmationCopy(args).title}
          trailing={
            <HeaderEnd>
              <CardStatus>Closed</CardStatus>
            </HeaderEnd>
          }
        />
      ) : (
        <ConfirmationResolvedNotice request={args} />
      );
  } else if (args.tool_name === 'run_agent_sample') {
    content = <TryConfirmationSurface request={args} />;
  } else if (tryIt && args.tool_name === 'run_agent_test') {
    content = (
      <AgentCard
        icon={iconFlask}
        title={confirmationCopy(args).title}
        trailing={
          <HeaderEnd>
            <CardStatus tone='warning'>
              <span role='alert'>Reload required</span>
            </CardStatus>
            <CardLinkButton onClick={() => window.location.reload()}>
              Reload
            </CardLinkButton>
          </HeaderEnd>
        }
      />
    );
  } else if (unlocked && args.tool_name === MERGE_DUPLICATES_ACTION) {
    // Off the flag the merge still waits for an answer, so it asks in the
    // plain confirmation below; the server checks again when it runs.
    content = <MergeDuplicatesConfirmationCard request={args} />;
  } else if (ONE_OFF_ACTIONS.has(args.tool_name)) {
    // A one-off play, a live run of an agent, a repeat or a draft agent: its
    // own card in every chat, the agents look's included.
    content = <OneOffConfirmationSurface request={args} />;
  } else if (unlocked && LIST_SHAPING_ACTIONS.has(args.tool_name)) {
    // A list column, its values or a skill run on a list: its own card in
    // every chat with Agents unlocked. Otherwise it asks in the plain
    // confirmation below, so the approval always has its buttons.
    content = <ListShapingConfirmationSurface request={args} />;
  } else if (args.type === 'list_disambiguation') {
    content = <AgentListPickCard request={args} />;
  } else if (args.tool_name === 'import_to_list') {
    content = <AgentImportConfirmationCard request={args} />;
  } else {
    // Every other confirmation, in every chat (the AppSumo-only chat's
    // included): turning an agent on or off, a send, a delete, and Clu's
    // sequence changes among them.
    content = <AgentConfirmationCard request={args} />;
  }

  return <div data-timeline-action>{content}</div>;
};

const ConfirmationRequestToolUi = makeAssistantToolUI<
  ConfirmationRequest,
  unknown
>({
  toolName: 'confirmation_request',
  render: ConfirmationRequestSurface,
});

export function CopilotToolUIs({
  onConfirm,
  onReject,
  pendingConfirmationId,
  pendingConfirmationActionToken = null,
  lists = [],
  defaultListId = null,
  children,
}: CopilotToolUiContextValue & { children?: ReactNode }) {
  const contextValue = useMemo(
    () => ({
      onConfirm,
      onReject,
      pendingConfirmationId,
      pendingConfirmationActionToken,
      lists,
      defaultListId,
    }),
    [
      onConfirm,
      onReject,
      pendingConfirmationId,
      pendingConfirmationActionToken,
      lists,
      defaultListId,
    ]
  );

  return (
    <CopilotToolUiContext.Provider value={contextValue}>
      <ToolResultToolUi />
      <ConfirmationRequestToolUi />
      {children}
    </CopilotToolUiContext.Provider>
  );
}
